import { deleteJob } from "@stryker/core/api/jobs";
import { QueryKeys } from "@stryker/core/queryKeys";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Dispatch, SetStateAction } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { ImportantNote } from "@/components/ui/importantNote";
import { Typography } from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";

type DeleteJobModalProps = {
  jobIdToRemove: number | null;
  setJobIdToRemove: Dispatch<SetStateAction<null | number>>;
};
export default function DeleteJobModal({ jobIdToRemove, setJobIdToRemove }: DeleteJobModalProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: deleteJob,
    onError: (error: string) => {
      toast({ variant: "destructive", title: "Error", description: error?.toString() });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.JobsList] });
      setJobIdToRemove(null);
      if (/^\/jobs\/\d+$/.test(location.pathname)) {
        navigate("/jobs");
      }
    },
  });

  return (
    <Dialog open={!!jobIdToRemove} onOpenChange={() => setJobIdToRemove(null)}>
      <DialogTitle className="hidden">Remove Job {jobIdToRemove}</DialogTitle>
      <DialogDescription className="hidden">Confirm deletion of this job and all associated quotes</DialogDescription>
      <DialogContent>
        <Typography variant="text-lg" weight="semibold" className="text-gray-900">
          Remove Job
        </Typography>
        <Typography variant="text-lg" className="text-gray-600">
          Are you sure you want to delete the job? You will permanently lose access to this job.
        </Typography>
        <ImportantNote>
          In addition to deleting this job, you will also delete any associated quote for this job. Do you wish to
          continue?
        </ImportantNote>
        <div className="mt-14 flex justify-between gap-4">
          <Button
            size="lg"
            disabled={isPending}
            variant="outline"
            className="flex-1"
            onClick={() => setJobIdToRemove(null)}
          >
            Cancel
          </Button>
          <Button
            disabled={isPending}
            size="lg"
            onClick={() => {
              if (!jobIdToRemove) return;
              mutate(jobIdToRemove);
            }}
            className="flex-1"
          >
            Remove
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
